<template>
  <o-modal
    ref="modal"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.saveChanges'), type: 'confirm', callback: handleConfirm },
    ]"
    :valid="validate"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg"
    content-class="mx-auto max-w-lg h-full py-4 px-4 flex flex-col"
  >
    <div v-if="serviceForm" class="text-left h-full flex flex-col">
      <div class="text-center">
        <h3 class="font-medium leading-6 text-md text-gray-700 dark:text-gray-200 mb-1">
          {{ $t('form.edit') }}
        </h3>

        <p class="text-sm leading-5 text-gray-600 dark:text-gray-300" v-html="$t('form.changeOptions', { title: serviceForm.form.title })" />
      </div>

      <div class="bg-white dark:bg-gray-900 border-t border-b -mx-8 mb-4 mt-6 flex-grow h-10">
        <o-scroll class="py-4 px-8 h-full">
          <o-form ref="form" class="h-full">
            <div class="flex space-x-4 mb-6">
              <o-field-group class="w-full" :label="$t('general.frequency')" :info="$t('form.frequencyInfo')">
                <select-field v-model="serviceForm.frequency" :options="serviceFormFrequencyOptions" />
              </o-field-group>
            </div>

            <div class="flex space-x-4 mb-6">
              <o-field-group class="w-full" :label="$t('service.requestMode')" :info="$t('service.requestModeInfo')">
                <select-field v-model="serviceForm.requirementMode" :options="serviceFormRequirementModeOptions" />
              </o-field-group>
            </div>

            <o-field-group
              v-if="isRequirementModeScheduled"
              v-slot="{ status }"
              :label="$t('form.scheduledTime')"
              :name="$t('form.scheduledTime')"
              :info="$t('form.scheduledTimeInfo')"
              class="mb-4 block"
              rules="required"
            >
              <div class="flex items-center space-x-2">
                <number-field
                  v-model="serviceForm.scheduleOffsetValue"
                  type="number"
                  :status="status"
                  class="w-1/2"
                />
                <segment-field v-model="serviceForm.scheduleOffsetMode" :options="scheduleOffsetOptions" class="border w-1/2" />
              </div>
            </o-field-group>
          </o-form>
        </o-scroll>
      </div>
    </div>
  </o-modal>
</template>

<script setup lang="ts">
import FormSelect from '@/components/form/elements/Select.vue'
import { OAlert, OFieldGroup, OForm } from '@/components/elements'
import { TextField, SelectField, NumberField, SegmentField } from '@/components/fields'

import {
  ServiceFormFrequency,
  ServiceFormFrequencyLabels,
  ServiceFormRequirementMode,
  ServiceFormRequirementModeLabels,
} from '~/_api/models/serviceForm'
import type { ServiceForm } from '~/_api/models/serviceForm'
import type { Service } from '~/_api/models/service'

const props = defineProps<{
  service: Service
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'error'): void
  (e: 'update:service', service: Service): void
  (e: 'update:loading', loading: boolean): void
}>()

const { $api, $notification, $i18n } = useNuxtApp()
const $t = $i18n.t.bind($i18n)

const modal = ref()
const form = ref()
const serviceForm = ref<ServiceForm | null>(null)

const serviceFormFrequencyOptions = computed(() => {
  return Object.values(ServiceFormFrequency)
    .filter((value): value is ServiceFormFrequency => typeof value === 'number')
    .map((value) => ({
      label: $t(ServiceFormFrequencyLabels[value]),
      value,
    }))
})

const serviceFormRequirementModeOptions = computed(() => {
  return Object.values(ServiceFormRequirementMode)
    .filter((value): value is ServiceFormRequirementMode => typeof value === 'number')
    .map((value) => ({
      label: $t(ServiceFormRequirementModeLabels[value]),
      value,
    }))
})

const scheduleOffsetOptions = computed(() => [
  { label: $t('form.before'), value: 'before' },
  { label: $t('form.after'), value: 'after' },
])

const isRequirementModeScheduled = computed(() => {
  return serviceForm.value?.isRequirementModeScheduled
})

const open = (form: ServiceForm) => {
  serviceForm.value = form
  modal.value?.open()
}

const validate = () => {
  return form.value?.validate()
}

const handleConfirm = async () => {
  emit('update:loading', true)

  try {
    const response = await $api.service.updateForm(props.service, serviceForm.value!)
    const service = response.data

    if (service) {
      $notification.toast({
        title: $t('form.success'),
        text: $t('form.serviceFormSuccessInfo', { title: serviceForm.value?.form?.title }),
        variant: 'success',
      })

      emit('success')
      emit('update:service', service)

      emit('update:loading', false)
      return true
    }
  } catch (e) {
    $notification.toast({
      title: $t('form.error'),
      text: $t('form.errorInfo', { title: serviceForm.value?.form?.title }),
    })

    emit('update:loading', false)
    emit('error')
  }
}

defineExpose({
  open,
})
</script>
